import classNames from "classnames";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Chip } from "components/DesignSystem/Chips/Chips";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { Filter } from "components/DesignSystem/Filter/Filter";
import { DateFilter } from "components/Filters/DateFilter";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { ChatBubble } from "components/icons/ChatBubble";
import { ActivityChart } from "components/icons/ActivityChart";
import { StarRating } from "components/icons/StarRating";
import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { useFilters } from "hooks/useFilter";
import { CsSummary, useGetCsDashboardQuery } from "store/apis/supportDashboard";
import { CsChatSection } from "./CsChatSection";
import { CsNpsSection } from "./CsNpsSection";
import { CsTicketsSection } from "./CsTicketsSection";
import { usePageTitle } from "hooks/usePageTitle";
import { Header } from "components/DesignSystem/Header/Header";

type DateFilters = {
  START_DATE: string;
  END_DATE: string;
  SELECT_PERIOD: string;
};

const defaultFilters: DateFilters = {
  START_DATE: dayjs().startOf("week").format(YYYY_MM_DD),
  END_DATE: dayjs().format(YYYY_MM_DD),
  SELECT_PERIOD: "CUSTOM" as string,
};

type SectionIconProps = {
  className: string;
  children: React.ReactNode;
};

const SectionIcon = ({ className, children }: SectionIconProps) => (
  <span
    className={classNames(
      "t-flex t-items-center t-justify-center t-rounded-lg t-h-7 t-w-7 t-shrink-0",
      className
    )}
  >
    {children}
  </span>
);

type SectionHeaderProps = {
  icon: React.ReactNode;
  title: string;
};

const SectionHeader = ({ icon, title }: SectionHeaderProps) => (
  <div className="t-flex t-items-center t-gap-2 t-mb-3">
    {icon}
    <span className="t-text-subtitle t-font-semibold t-text-text-100">
      {title}
    </span>
  </div>
);

const PageSkeleton = () => (
  <div className="t-px-6 t-py-6 t-flex t-flex-col t-gap-8">
    {Array.from({ length: 3 }).map((_, i) => (
      <div key={i} className="t-flex t-flex-col t-gap-3">
        <Skeleton className="t-h-5 t-w-40" />
        <div className="t-grid t-grid-cols-3 t-gap-3">
          {Array.from({ length: 3 }).map((_, j) => (
            <Skeleton key={j} className="t-h-24 t-rounded-lg" />
          ))}
        </div>
        <Skeleton className="t-h-64 t-rounded-lg" />
      </div>
    ))}
  </div>
);

type SupportDashboardContentProps = {
  summary: CsSummary | undefined;
  agentCount: number;
  dateFrom: string;
  dateTo: string;
};

const SupportDashboardContent = ({
  summary,
  agentCount,
  dateFrom,
  dateTo,
}: SupportDashboardContentProps) => {
  if (!summary) return null;

  return (
    <div className="t-px-6 t-py-6 t-flex t-flex-col t-gap-8">
      <section>
        <SectionHeader
          icon={
            <SectionIcon className="t-bg-purple-10 t-text-purple-70">
              <ChatBubble />
            </SectionIcon>
          }
          title="Chat"
        />
        <CsChatSection
          summary={summary.chat}
          agentCount={agentCount}
          dateFrom={dateFrom}
          dateTo={dateTo}
        />
      </section>

      <section>
        <SectionHeader
          icon={
            <SectionIcon className="t-bg-blue-10 t-text-blue-70">
              <ActivityChart />
            </SectionIcon>
          }
          title="Tickets"
        />
        <CsTicketsSection
          summary={summary.tickets}
          dateFrom={dateFrom}
          dateTo={dateTo}
        />
      </section>

      <section>
        <SectionHeader
          icon={
            <SectionIcon className="t-bg-green-10 t-text-dark_green-70">
              <StarRating />
            </SectionIcon>
          }
          title="NPS submissions"
        />
        <CsNpsSection
          summary={summary.nps}
          dateFrom={dateFrom}
          dateTo={dateTo}
        />
      </section>
    </div>
  );
};

export const SupportDashboard = () => {
  usePageTitle("Performance dashboard");
  const { values: filter, updateFilter } = useFilters<DateFilters>({
    initialValue: defaultFilters,
    useQueryParams: true,
  });

  const { data, isLoading, isSuccess } = useGetCsDashboardQuery({
    start_date: filter.START_DATE,
    end_date: filter.END_DATE,
  });

  const agentCount = data?.total_agents ?? 0;
  const isEmpty = isSuccess && !data;

  return (
    <DashboardContainer className="t-h-full">
      <DashboardContainer.Header sticky className="t-px-6 t-py-4">
        <div className="t-flex t-flex-col t-gap-4">
          <Header v2 title="Support Team Performance" />
          <Filter.Root
            defaultValue="dateRange"
            title="Filter"
            capsule={
              <Chip isActive isRemovable={false} filterType="dateRange">
                {dayjs(filter.START_DATE).format("MMM DD, YYYY")} –{" "}
                {dayjs(filter.END_DATE).format("MMM DD, YYYY")}
              </Chip>
            }
          >
            <Filter.Portal>
              <Filter.List>
                <Filter.ListItem value="dateRange">Date Range</Filter.ListItem>
              </Filter.List>
              <Filter.Body value="dateRange" block>
                <DateFilter values={filter} updateFilter={updateFilter} />
              </Filter.Body>
            </Filter.Portal>
          </Filter.Root>
        </div>
      </DashboardContainer.Header>

      <DashboardContainer.Content>
        <Async.Root
          isLoading={isLoading}
          isSuccess={isSuccess}
          isEmpty={isEmpty}
          customLoader={<PageSkeleton />}
        >
          <Async.Success>
            <SupportDashboardContent
              summary={data?.summary}
              agentCount={agentCount}
              dateFrom={filter.START_DATE}
              dateTo={filter.END_DATE}
            />
          </Async.Success>

          <Async.Empty>
            <div className="t-flex t-items-center t-justify-center t-flex-1 t-text-text-30 t-text-body-sm t-h-full">
              No data available for the selected period.
            </div>
          </Async.Empty>
        </Async.Root>
      </DashboardContainer.Content>
    </DashboardContainer>
  );
};
